if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.productList = document.querySelector(`ul[data-id="${this.sectionId}"]`);
      this.loadMoreButton = this.querySelector('.btn--load-more');
      this.paginationType = this.getAttribute('data-type');
      this.countItem = parseInt(this.productList.getAttribute('data-products-count'), 10);
      this.currentPage = 2;
      this.scrollObserver = null;
      this.isLoading = false;
      this.pageCache = new Map();
      this.ajaxSectionId = 'ajax-collection-products';
      
      this.sectionWidth = this.productList.getAttribute('data-section-width') || 'container';
      this.filterType = this.productList.getAttribute('data-filter-type') || 'vertical';
      this.columnsDesktop = this.productList.getAttribute('data-columns-desktop') || '4';
      this.productsPerPage = this.productList.getAttribute('data-products-per-page') || '16';
      
      this.animationOrder = this.productList.children.length;
      this.needsRecheck = false;
    }

    connectedCallback() {
      this.productList?.addEventListener('click', (e) => {
        const link = e.target.closest('.grid__item a');
        if (link) {
          const state = JSON.stringify({
            scrollY: window.scrollY,
            page: this.currentPage - 1
          });
          sessionStorage.setItem('pagination-state', state);
        }
      });

      this.paginationType === 'load-more-button'
        ? this.setupLoadMoreButton()
        : this.paginationType === 'infinite-scroll'
        ? this.setupInfiniteScroll()
        : null;

      this.restoreScrollState();
    }

    setupLoadMoreButton() {
      this.loadMoreButton?.addEventListener('click', () => this.loadNextPage());
    }

    setupInfiniteScroll() {
      this.scrollObserver = new IntersectionObserver(
        entries => {
          const entry = entries[0];
          
          if (entry.isIntersecting && !this.isLoading) {
            this.loadNextPage();
          }
          
          if (entry.isIntersecting && this.isLoading) {
            this.needsRecheck = true;
          }
        },
        { rootMargin: '300px', threshold: 0 }
      );
      this.scrollObserver.observe(this);
    }

    buildAjaxUrl() {
      const url = new URL(window.location.href);
      
      url.searchParams.set('page', this.currentPage);
      url.searchParams.set('sections', this.ajaxSectionId);
      url.searchParams.set('section_width', this.sectionWidth);
      url.searchParams.set('filter_type', this.filterType);
      url.searchParams.set('columns_desktop', this.columnsDesktop);
      url.searchParams.set('products_per_page', this.productsPerPage);
      
      return url.toString();
    }

    async loadNextPage() {
      if (this.isLoading) return;

      try {
        this.isLoading = true;
        this.needsRecheck = false;
        this.toggleLoadingSpinner(true);

        let productsHTML;
        
        if (this.pageCache.has(this.currentPage)) {
          productsHTML = this.pageCache.get(this.currentPage);
        } else {
          const url = this.buildAjaxUrl();
          const response = await fetch(url);
          const data = await response.json();
          
          productsHTML = this.stripSectionWrapper(data[this.ajaxSectionId]);
          
          if (this.pageCache.size > 5) {
            const firstKey = this.pageCache.keys().next().value;
            this.pageCache.delete(firstKey);
          }
          this.pageCache.set(this.currentPage, productsHTML);
        }

        this.renderFetchedProducts(productsHTML);
        this.currentPage++;
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        this.toggleLoadingSpinner(false);
        this.isLoading = false;
        
        requestAnimationFrame(() => {
          if (this.needsRecheck) {
            this.checkAndLoadMore();
          }
        });
      }
    }

    checkAndLoadMore() {
      const rect = this.getBoundingClientRect();
      const isVisible = (
        rect.top < window.innerHeight + 300 &&
        rect.bottom > 0
      );
      
      if (isVisible && !this.isLoading && this.productList.children.length < this.countItem) {
        this.loadNextPage();
      }
    }

    stripSectionWrapper(html) {
      if (!html) return '';
      
      const regexMatch = html.match(/<div[^>]*shopify-section[^>]*>([\s\S]*)<\/div>\s*$/i);
      if (regexMatch && regexMatch[1]) {
        return regexMatch[1].trim();
      }
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html.trim();
      const sectionWrapper = tempDiv.querySelector('.shopify-section');
      
      return sectionWrapper ? sectionWrapper.innerHTML : html;
    }

    renderFetchedProducts(html) {
      if (!html || html.trim().length === 0) {
        this.finalizePagination();
        return;
      }

      html = this.updateAnimationOrder(html);
      this.productList.insertAdjacentHTML('beforeend', html);

      if (this.productList.children.length >= this.countItem) {
        this.finalizePagination();
      }
    }

    updateAnimationOrder(html) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      const items = tempDiv.querySelectorAll('.grid__item[data-cascade]');
      items.forEach((item) => {
        this.animationOrder++;
        item.style.setProperty('--animation-order', this.animationOrder);
      });
      
      return tempDiv.innerHTML;
    }

    toggleLoadingSpinner(isLoading) {
      if (!this.loadMoreButton) return;
      const spinner = this.loadMoreButton.querySelector('.loading__spinner');
      
      if (isLoading) {
        this.loadMoreButton.classList.add('loading');
        spinner?.classList.remove('hidden');
      } else {
        this.loadMoreButton.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    }

    finalizePagination() {
      this.scrollObserver?.unobserve(this);
      this.scrollObserver?.disconnect();
      this.loadMoreButton?.classList.add('visually-hidden');
    }

    disconnectedCallback() {
      this.scrollObserver?.disconnect();
    }

    async restoreScrollState() {
      const stateStr = sessionStorage.getItem('pagination-state');
      if (!stateStr) return;

      try {
        const { scrollY, page } = JSON.parse(stateStr);
        const savedPage = parseInt(page, 10);

        if (!isNaN(savedPage) && savedPage >= 2) {
          await this.loadPagesInParallel(savedPage);

          requestAnimationFrame(() => {
            window.scrollTo(0, scrollY);
            sessionStorage.removeItem('pagination-state');
          });
        }
      } catch (e) {
        sessionStorage.removeItem('pagination-state');
      }
    }

    async loadPagesInParallel(targetPage) {
      const BATCH_SIZE = 3;
      const pagesToLoad = [];

      while (this.currentPage <= targetPage) {
        pagesToLoad.push(this.currentPage);
        this.currentPage++;
      }

      for (let i = 0; i < pagesToLoad.length; i += BATCH_SIZE) {
        const batch = pagesToLoad.slice(i, i + BATCH_SIZE);
        
        const batchPromises = batch.map(page => {
          const url = new URL(window.location.href);
          url.searchParams.set('page', page);
          url.searchParams.set('sections', this.ajaxSectionId);
          url.searchParams.set('section_width', this.sectionWidth);
          url.searchParams.set('filter_type', this.filterType);
          url.searchParams.set('columns_desktop', this.columnsDesktop);
          url.searchParams.set('products_per_page', this.productsPerPage);

          return fetch(url.toString())
            .then(res => res.json())
            .then(data => ({ 
              page, 
              html: this.stripSectionWrapper(data[this.ajaxSectionId]) 
            }));
        });

        const batchResults = await Promise.all(batchPromises);
        
        batchResults.forEach(({ page, html }) => {
          this.pageCache.set(page, html);
          this.renderFetchedProducts(html);
        });
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
